import { useCallback, useContext } from "react";
import type { Location } from "history";
import { EventProperties } from "@segment/analytics-next";
import pick from "lodash/pick";

import { AnalyticsContext } from "src/providers/analytics-provider";
import { LicensedToken } from "src/components/pages/Home/license";

const defaultOptions = { context: { direct: true } };

export const useAnalytics = () => {
  const { analytics } = useContext(AnalyticsContext);

  const pageViewed = useCallback(
    (name: string, page: Location) => analytics?.page(name, page, defaultOptions).catch(() => {}),
    [analytics],
  );

  const trackRedirect = useCallback(
    (url: string) => analytics?.track("Redirect", { url }, defaultOptions).catch(() => {}),
    [analytics],
  );

  const track = useCallback(
    (action: string, payload: EventProperties = {}) =>
      analytics?.track(action, payload, defaultOptions).catch(() => {}),
    [analytics],
  );

  const trackError = useCallback(
    (action: string, payload: EventProperties = {}) => track(`[ERROR]: ${action}`, { type: "error", ...payload }),
    [track],
  );

  const trackButtonClicked = useCallback(
    (title: string, payload: EventProperties = {}) => track("Button Clicked", { ...payload, title }),
    [track],
  );

  const trackLinkClicked = useCallback(
    (title: string, payload: EventProperties = {}) =>
      track("Link Clicked", { ...payload, title })?.then((response) => response), // TODO: Track redirect if external site,
    [track],
  );

  const trackAnchorElement = useCallback(
    (element: HTMLAnchorElement, title: string, payload: EventProperties = {}) => {
      if (!analytics?.trackLink) {
        return false;
      }

      // TODO: How to untrack if disabled?
      if (!analytics?.trackLink) {
        return false;
      }

      // TODO: How to untrack if disabled?
      const attributes = pick(element, ["href", "target", "rel", "disabled"]);

      analytics
        .trackLink(element, "Link Clicked", {
          ...attributes,
          ...payload,
          title,
        })
        .then((response) => response); // TODO: Track redirect if external site

      return true;
    },
    [analytics],
  );

  const trackFeedback = useCallback(
    ({ category, type }: { category: string; type: string }) => track("Feedback Sent", { category, type }),
    [track],
  );

  const identifyUser = useCallback(
    (token: LicensedToken | null) => {
      const currentId = analytics?.user().id();
      const { sub: id, ...user } = pick(token, [
        "license_type",
        "license_exp",
        "license_iat",
        "license_trial",
        "preferred_username",
        "email",
        "email_verified",
        "sub",
      ]);

      if (currentId === id) {
        // No change in user
        return Promise.resolve();
      }

      if (id) {
        // User updated, has id, identify
        return analytics?.identify(id, { id, ...user }, { context: { direct: true } }).catch(() => {});
      }

      // User updated, no id, unidentify (reset)
      return analytics?.reset();
    },
    [analytics],
  );

  return {
    pageViewed,
    trackRedirect,
    track,
    trackError,
    trackButtonClicked,
    trackLinkClicked,
    trackAnchorElement,
    trackFeedback,
    identifyUser,
  };
};
